import { toIsoDate } from './localDate';
import { FoodRecognitionResponse, DetectedFoodItem, FoodCategory, QuantityEstimate, VisibleCondition, ShelfLifeBucket, ShelfLifeEstimate } from '../types/foodRecognition';
import { SHELF_LIFE_PROMPT_BLOCK, SHELF_LIFE_PROMPT_EXAMPLES } from './shelfLifePrompt';
import { PantryLocation } from './pantryLocation';

/**
 * Receipt-specific response format from AI
 */
export interface ReceiptMetadata {
  store_name?: string;
  purchase_date?: string; // YYYY-MM-DD
  total_amount?: number;
  currency?: string;
  purchase_date_needs_review?: boolean; // true when the detected date was missing or failed validation
}

/**
 * Format a Date as a local-time YYYY-MM-DD string.
 *
 * This used to be the codebase's only correct implementation, tucked away in
 * the receipt parser while the planner rendered dates in UTC (#679). It now
 * re-exports the shared one under the name the receipt code already uses.
 */
export const formatLocalDateISO = toIsoDate;

/**
 * Parse a YYYY-MM-DD receipt date as a LOCAL calendar date.
 *
 * `new Date('2026-07-17')` is parsed as UTC midnight, so anywhere west of
 * Greenwich it renders as the previous day — a receipt bought on the 17th
 * displayed as the 16th. Returns null when the string isn't a plain date.
 */
export const parseLocalDateISO = (iso: string | undefined | null): Date | null => {
  if (!iso) return null;
  const match = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
};

export interface ReceiptDetectedItem {
  name: string;
  category: string;
  confidence: number;
  quantity_estimate: string;
  unit_price?: number;
  total_price?: number;
  expiry_category?: ShelfLifeBucket | ShelfLifeEstimate; // Legacy values still accepted on the way in
  receipt_line?: string;
}

export interface ReceiptAnalysisNotes {
  image_quality: string;
  text_readability: string;
  total_items_detected: number;
  food_items_count: number;
  non_food_items_skipped: number;
}

export interface ReceiptRecognitionResponse {
  receipt_metadata?: ReceiptMetadata;
  detected_items: ReceiptDetectedItem[];
  analysis_notes: ReceiptAnalysisNotes;
}

/**
 * Gets the specialized receipt parser prompt for Gemini
 * @param language - The language to use for ingredient names ('en' or 'fr')
 */
export const getReceiptParserPrompt = (
  language: 'en' | 'fr' = 'en',
  todayISO: string = formatLocalDateISO(new Date()),
): string => {
  const currentYear = parseInt(todayISO.slice(0, 4), 10);
  const languageInstructions = language === 'fr'
    ? `IMPORTANT - LANGUAGE: Return ALL ingredient names in FRENCH (français).
   • "ORG BNNA" → "Bananes biologiques" (NOT "Organic Bananas")
   • "2% MILK" → "Lait 2%" (NOT "2% Milk")
   • "GRND BEEF" → "Boeuf haché" (NOT "Ground Beef")
   • "RED PEPPER" → "Poivron rouge" (NOT "Red Bell Pepper")
   • Use proper French names for all food items`
    : `IMPORTANT - LANGUAGE: Return ALL ingredient names in ENGLISH.
   • "ORG BNNA" → "Organic Bananas"
   • "2% MILK" → "2% Milk"
   • "GRND BEEF" → "Ground Beef"`;

  return `You are a specialized grocery receipt parser AI assistant designed to analyze grocery store receipt photos and extract food inventory data. Your task is to identify all food items purchased from the receipt text with high precision and return the results ONLY in the specified JSON format.

${languageInstructions}

Analysis Guidelines:
1. Text Recognition: Carefully read all text on the receipt, including item names, quantities, prices, and dates.
2. Item Extraction: Extract each food item listed on the receipt. Skip non-food items (cleaning supplies, toiletries, epsom salts, kitchenware, etc.).
3. Item Name Normalization: Clean up receipt abbreviations and codes to create readable item names

CRITICAL - RECEIPT LINE TYPES (warehouse/big-box stores like Costco print several NON-ITEM line types):
Most warehouse receipt lines start with a numeric item code. Classify every printed line BEFORE extracting:

a) DISCOUNT/REBATE LINES — a line whose name is "TPD/<item code>" with an amount ending in "-":
   • "2087174 TPD/2445711 2,50-" = a $2.50 instant rebate applied to the item with code 2445711.
   • NEVER output a TPD/rebate line as a food item. It is a price adjustment, not a product.

b) VOIDED ITEMS — a line reading "ANNUL" (or "VOID") followed by a REPEAT of an earlier item line with the amount ending in "-":
   • "ANNUL" then "2056187 SALADASIAN2P 8,99-" cancels ONE of the "2056187 SALADASIAN2P 8,99" lines above.
   • Subtract each voided line from that item's quantity (3 identical lines + 1 void = quantity 2).
   • Never output the ANNUL line or the negative repeat as items.

c) FEE/DEPOSIT LINES — never food items, always skip: "CONSIGNE QC" / "CONSIGNE" (bottle deposit), "WECOFRAIS" / "ECOFRAIS" / "ENVIRO" (environmental fees), "DEPOT".

d) SECTION MARKERS & TOTALS — skip: "Bas du panier", "Compte bas du panier N", "SOUS-TOTAL", "TPS"/"TVQ"/"TVH"/"TAXE", "TOTAL", payment/card lines.

e) TAX FLAGS — a trailing "FP", "F" or "P" after a price is a tax code. It is not part of the product name or the price.

CRITICAL - REPEATED LINES = QUANTITY (warehouse receipts print one line per unit):
   • "30669 BANANES 0,99" printed twice → ONE entry, quantity_estimate "2", total_price 1.98.
   • "2445711 CRAQUELINS 11,49" printed twice → ONE entry, quantity_estimate "2", total_price 22.98.
   • Count the lines with the same item code, subtract ANNUL voids, and output ONE entry per product.
   • Do NOT output duplicate entries for the same code, and do NOT collapse multiple lines into quantity 1.
   • Only genuinely different products (different codes) get separate entries.
   • BE CONSERVATIVE: quantity above 1 is ONLY justified by literally counting N separate printed lines
     with the same code. A single printed line = quantity from the name (weight/volume/count) or 1.
     Worked example: three "2056187 SALADASIAN2P 8,99" lines followed by ANNUL + "2056187 SALADASIAN2P 8,99-"
     → ONE entry, quantity_estimate "2" (3 lines − 1 void).

WAREHOUSE NAME ABBREVIATIONS (expand abbreviated ALL-CAPS names into real product names):
   • "KS" prefix = Kirkland Signature (Costco store brand): "KS CREV.ROUG" → red shrimp / crevettes rouges, "KS 21WGRAIN" → 21 whole grain bread.
   • Quebec receipts abbreviate French heavily: "CHEERIOS MIE" → Cheerios miel, "SAUMON FUME" → saumon fumé,
     "POITRINE DES" → poitrine de poulet désossée, "HAUT CUISSE" → hauts de cuisse de poulet, "VEAU HACHE" → veau haché,
     "BLOC CHEDDR" → bloc de cheddar, "JAMBON CUIT" → jambon cuit, "PATES OEUFS" → pâtes aux oeufs, "BLEUETS" → bleuets.
   • Expand to the most plausible real grocery product. Keep the original text in receipt_line. If your expansion is a guess, lower the confidence score — do not invent exotic products from unreadable text.

EMBEDDED PACK SIZES IN NAMES:
   • Trailing size glued to the name: "KIWIDORE3LB" → name "Kiwis dorés", quantity_estimate "3 lb".
   • "CITRON 5 LB" → name "Citrons", quantity_estimate "5 lb".
   • Multi-pack descriptors like "2P", "2PK", "4X100" are pack formats, NOT quantities:
     "SALADASIAN2P" → name "Salade asiatique (2pk)", quantity from the repeated-lines rule.
     "CAPRES 4X100" → name "Câpres (4x100g)", quantity_estimate "1".

COMPLETENESS SELF-CHECK (do this before you answer):
   1. Go line by line from the top of the receipt to the total. EVERY printed line must be accounted for as exactly one of: food item, non-food item (skipped), discount, void, fee, section marker, or metadata.
   2. Sum your detected items' total_price values, add fee lines, subtract discounts: the result should approximate the printed SOUS-TOTAL. If it is off by more than ~2%, you missed lines or misread prices — re-scan the receipt before answering.
   3. receipt_metadata.total_amount = the printed grand TOTAL (after taxes).
4. CRITICAL - Weight/Quantity Extraction:

   LOOK AT THE ITEM NAME — if you see a NUMBER followed by a UNIT, extract it into quantity_estimate and REMOVE it from the name.

   WEIGHT EXAMPLES (follow exactly):
   • "PATATES 1.180kg" → name: "Potatoes", quantity_estimate: "1.180 kg"
   • "RAISINS ROUGES 1.195kg" → name: "Red Raisins", quantity_estimate: "1.195 kg"
   • "BANANES 0.500kg" → name: "Bananas", quantity_estimate: "0.500 kg"
   • "TOMATES 0.850kg" → name: "Tomatoes", quantity_estimate: "0.850 kg"
   • "BOEUF HACHE 0.5kg" → name: "Ground Beef", quantity_estimate: "0.5 kg"
   • "CAROTTES 250g" → name: "Carrots", quantity_estimate: "250 g"

   VOLUME EXAMPLES — THIS IS THE MOST COMMON MISTAKE, PAY ATTENTION:
   • "LAIT 2L" → name: "Milk", quantity_estimate: "2 L"
   • "LAIT HOMO 2L" → name: "Homo Milk", quantity_estimate: "2 L"
   • "LAIT 2% 2L" → name: "Milk 2%", quantity_estimate: "2 L"
   • "JUS ORANGE 1L" → name: "Orange Juice", quantity_estimate: "1 L"
   • "JUS 500ml" → name: "Juice", quantity_estimate: "500 ml"
   • "BOISSON 1.5L" → name: "Beverage", quantity_estimate: "1.5 L"
   • "EAU MINERALE 1L" → name: "Mineral Water", quantity_estimate: "1 L"

   COUNT EXAMPLES:
   • "POIVRONS 3x" → name: "Bell Peppers", quantity_estimate: "3"
   • "OEUFS" → name: "Eggs", quantity_estimate: "1"

   RULES:
   1. Extract the NUMBER and UNIT from the item name
   2. Add a SPACE between number and unit: "2 L" not "2L", "1.180 kg" not "1.180kg"
   3. REMOVE the weight/volume from the item name — the name should be clean
   4. If no weight/volume/count found, use "1"
   5. NEVER leave a volume like "2L" in the name — always move it to quantity_estimate
5. Category Classification: Assign each item to one of these categories:
   • Fruits | Vegetables | Dairy | Meat | Seafood | Beverages | Condiments | Frozen Foods | Bread/Grains | Snacks | Other
6. Price Extraction: Extract the unit price or total price for each item.
7. Purchase Date: Find and extract the purchase date from the receipt (usually at the top or bottom).

   TODAY IS ${todayISO} (current year ${currentYear}).
   RECENCY RULE (CRITICAL): A grocery receipt is photographed within days of shopping. The purchase
   date is ON OR BEFORE TODAY and is almost always within the last 30 days. Before returning a date:
   • A date in the FUTURE is impossible — you misread the year, or swapped the day and month. Re-examine.
   • A date more than ~12 months before today is almost certainly a misread year. Re-examine the digits.
   • Never emit a year earlier than ${currentYear - 1} unless the full 4-digit year is printed
     unmistakably on the receipt. A faint or ambiguous year should resolve to the most recent plausible year.

   DATE FORMAT DETECTION (CRITICAL - READ CAREFULLY):

   STEP 1: A 3-part date string (e.g. "26/06/04") can be in SEVERAL orders. Consider ALL of them:
   • "DD/MM/YY" — day first (common on European / French / Canadian receipts)
   • "MM/DD/YY" — month first (American)
   • "YY/MM/DD" — YEAR first (VERY common on payment-terminal / "DateTime" / transaction lines)
   • "YYYY-MM-DD" — ISO (already correct)
   Do NOT assume one order from the first digit alone — the same digits can be read multiple ways.

   STEP 2: DISAMBIGUATE BY RECENCY (this is the most important rule, it overrides everything below).
   1. Build EVERY valid reading of the digits — try day-first, month-first, AND year-first.
   2. Discard impossible ones (month > 12, or a day that doesn't exist for that month).
   3. Discard any reading in the FUTURE (after TODAY ${todayISO}).
   4. Of the readings that remain, RETURN THE ONE CLOSEST TO TODAY. A grocery receipt is recent.

   Worked example — "26/06/04" on a Provigo "DateTime:" line, TODAY ${todayISO}:
   • Day-first  26/06/04 → 2004-06-26 (or 2024-06-26) → ~2 years ago
   • Year-first 26/06/04 → 2026-06-04 → only 7 days ago  ← CLOSEST, not future → RETURN "2026-06-04"

   Tie-breakers, only when two readings are equally recent:
   • A number > 12 is the DAY or the YEAR — never the month.
   • French / Canadian stores (Provigo, Maxi, Metro, IGA, Loblaws, Sobeys) favour day-first over month-first.
   • If, after all this, the date is still genuinely ambiguous, return null rather than guessing an old date.

   STEP 3: Convert any 2-digit year to 4-digit using RECENCY (TODAY is ${todayISO}):
   • Pick the 4-digit year closest to today WITHOUT being in the future: use 20YY if 20YY ≤ ${currentYear}, else 19YY.
   • In almost all cases the year is ${currentYear} or ${currentYear - 1}. A receipt is NOT from 2004 or 2020
     unless that full 4-digit year is printed unmistakably — re-read the digits before settling on an old year.

   STEP 4: Return in YYYY-MM-DD format (or null if truly unreadable/ambiguous).

   Examples (TODAY ${todayISO}):
   OK    "26/06/04" + Provigo DateTime line → "2026-06-04" (year-first reading is closest to today)
   OK    "15/03/26" + French receipt → "2026-03-15" (15 can only be the day; day-first)
   OK    "06/04/26" + American store → "2026-06-04" (month-first; closest non-future reading)
   WRONG "26/06/04" → "2004-06-26" (WRONG — picked a 2-year-old reading over a 7-day-old one!)

8. ${SHELF_LIFE_PROMPT_BLOCK}

   ${SHELF_LIFE_PROMPT_EXAMPLES}

9. Store Information: Extract store name if visible.

Response Format:
Respond ONLY with valid JSON in this exact format. Do not include any explanatory text, markdown formatting, or additional commentary.

{
  "receipt_metadata": {
    "store_name": "string (optional)",
    "purchase_date": "YYYY-MM-DD (optional)",
    "total_amount": "number (optional)",
    "currency": "string (e.g., 'CAD', 'USD') (optional)"
  },
  "detected_items": [
    {
      "name": "string (normalized item name - REMOVE weight/volume from name if present)",
      "category": "Fruits | Vegetables | Dairy | Meat | Seafood | Beverages | Condiments | Frozen Foods | Bread/Grains | Snacks | Other",
      "confidence": "number (0-100, how certain you are this is the correct item name and category)",
      "quantity_estimate": "string with number and unit (e.g., '1.195 kg', '500 g', '2 L', '3') - extract from item name if present",
      "unit_price": "number (optional)",
      "total_price": "number (optional)",
      "expiry_category": "highly-perishable | perishable | week-plus | keeps-weeks | shelf-stable (REQUIRED for all food items)",
      "opened": "boolean (true when the package is open or the item is cut)",
      "receipt_line": "string (original text from receipt, optional)"
    }
  ],
  "analysis_notes": {
    "image_quality": "Poor | Fair | Good | Excellent",
    "text_readability": "Poor | Fair | Good | Excellent",
    "total_items_detected": "number",
    "food_items_count": "number",
    "non_food_items_skipped": "number"
  }
}

Important Notes:
- Only include food and beverage items in detected_items
- Skip non-food items (cleaning supplies, paper products, toiletries, etc.)
- NEVER include discount (TPD), void (ANNUL), fee (CONSIGNE/WECOFRAIS/ECOFRAIS), or total/tax lines as items
- expiry_category is REQUIRED for all food items - assign based on item type (fast, no calculations needed)
- receipt_line field helps users verify the extraction but is optional
- For ambiguous items, use your best judgment and reflect uncertainty in the confidence score
- Same item code on multiple lines = one entry with the aggregated quantity (see repeated-lines rule). Different codes or genuinely different products = separate entries

Example Item Transformations:
- "PATATES 1.180kg" → name: "Potatoes", quantity_estimate: "1.180 kg"
- "RAISINS ROUGES 1.195kg" → name: "Red Raisins", quantity_estimate: "1.195 kg"
- "BANANES 0.500kg" → name: "Bananas", quantity_estimate: "0.500 kg"
- "LAIT 2L" → name: "Milk", quantity_estimate: "2 L"
- "BOEUF HACHE 0.5kg" → name: "Ground Beef", quantity_estimate: "0.5 kg"
- "CAROTTES 250g" → name: "Carrots", quantity_estimate: "250 g"
- "POIVRONS 3x" → name: "Bell Peppers", quantity_estimate: "3"
- "OEUFS" → name: "Eggs", quantity_estimate: "1"`;
};

/**
 * Where a receipt category puts the food, when the category itself says so.
 *
 * `FoodCategory` has no frozen member, so `mapReceiptCategory` has to collapse
 * "Frozen Foods" into `Packaged Foods` — which the pantry files on a shelf, and
 * files *confidently*, so it never even reaches the "to sort" tray. Frozen peas
 * ended up next to the crisps with no expiry date at all.
 *
 * The category mapping is left alone; the storage signal is recovered here and
 * travels as a location, which outranks every keyword rule downstream.
 */
const receiptCategoryLocation = (category: string): PantryLocation | undefined => {
  const normalized = category.toLowerCase().trim();
  return normalized === 'frozen foods' || normalized === 'frozen' ? 'freezer' : undefined;
};

/**
 * Convert receipt category to standard FoodCategory
 */
const mapReceiptCategory = (category: string): FoodCategory => {
  const normalizedCategory = category.toLowerCase().trim();

  const categoryMap: Record<string, FoodCategory> = {
    'fruits': 'Fruits',
    'fruit': 'Fruits',
    'vegetables': 'Vegetables',
    'vegetable': 'Vegetables',
    'veggies': 'Vegetables',
    'dairy': 'Dairy',
    'meat': 'Meat',
    'meats': 'Meat',
    'seafood': 'Meat', // Map seafood to Meat for now, or add new category if needed
    'fish': 'Meat',
    'beverages': 'Beverages',
    'beverage': 'Beverages',
    'drinks': 'Beverages',
    'condiments': 'Condiments',
    'condiment': 'Condiments',
    'sauces': 'Condiments',
    'frozen foods': 'Packaged Foods',
    'frozen': 'Packaged Foods',
    'bread/grains': 'Bread/Grains',
    'bread': 'Bread/Grains',
    'grains': 'Bread/Grains',
    'snacks': 'Packaged Foods',
    'snack': 'Packaged Foods',
  };

  return categoryMap[normalizedCategory] || 'Other';
};

/**
 * Parse quantity string to extract number and unit
 * Examples: "1.195 kg" → {quantity: 1.195, unit: 'kg'}
 *           "500 g" → {quantity: 500, unit: 'g'}
 *           "3" → {quantity: 3, unit: 'piece'}
 */
export const parseQuantityWithUnit = (quantityStr: string): { quantity: number; unit: string } => {
  const normalized = quantityStr.toLowerCase().trim();

  // Weight patterns: "1.195 kg", "500g", "1.5 lb"
  const weightMatch = normalized.match(/(\d+\.?\d*)\s*(kg|g|lb|lbs|oz)(?![a-zA-Z])/);
  if (weightMatch) {
    const quantity = parseFloat(weightMatch[1]);
    let unit = weightMatch[2];
    // Normalize units
    if (unit === 'lbs') unit = 'lb';
    return { quantity, unit };
  }

  // Volume patterns: "2L", "500 ml", "1 cup"
  const volumeMatch = normalized.match(/(\d+\.?\d*)\s*(l|liter|ml|cup|cups)(?![a-zA-Z])/);
  if (volumeMatch) {
    const quantity = parseFloat(volumeMatch[1]);
    let unit = volumeMatch[2];
    if (unit === 'liter') unit = 'l';
    if (unit === 'cups') unit = 'cup';
    return { quantity, unit };
  }

  // Count: "3", "12", "single"
  if (normalized === 'single' || normalized === '1') {
    return { quantity: 1, unit: 'piece' };
  }
  if (normalized === 'dozen' || normalized === '12') {
    return { quantity: 12, unit: 'piece' };
  }
  const countMatch = normalized.match(/^(\d+)$/);
  if (countMatch) {
    return { quantity: parseInt(countMatch[1]), unit: 'piece' };
  }

  // Default
  return { quantity: 1, unit: 'piece' };
};

// ~13 month window: a grocery purchase date older than this is almost certainly a misread year.
const MAX_RECEIPT_AGE_MS = 1000 * 60 * 60 * 24 * 400;

/**
 * Validate, and where possible repair, the purchase date the AI extracted from a receipt.
 *
 * A grocery receipt is photographed within days of shopping, so the date must be on or before
 * today and recent. This deterministic guardrail catches what the prompt's recency reasoning
 * misses (e.g. a confidently misread "2020"). It never silently invents a date — when the value
 * is missing, unparseable, in the future and unrepairable, or implausibly old, it flags
 * `needsReview` so the UI can ask the user to confirm.
 *
 * @returns `date` — the cleaned YYYY-MM-DD value (undefined when unusable);
 *          `needsReview` — true when the user should confirm the date before saving.
 */
export const sanitizePurchaseDate = (
  raw: string | undefined | null,
  today: Date = new Date(),
): { date?: string; needsReview: boolean } => {
  if (!raw || typeof raw !== 'string') {
    return { needsReview: true };
  }

  const match = raw.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return { needsReview: true };
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  // Build in local time and confirm the calendar values round-trip (rejects 2024-02-31, month 13, etc.).
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return { needsReview: true };
  }

  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Future date: most often a swapped day/month (DD/MM vs MM/DD). Try the swap before giving up.
  if (parsed.getTime() > startOfToday.getTime()) {
    if (day >= 1 && day <= 12) {
      const swapped = new Date(year, day - 1, month);
      const swappedAge = startOfToday.getTime() - swapped.getTime();
      if (swappedAge >= 0 && swappedAge <= MAX_RECEIPT_AGE_MS) {
        return { date: formatLocalDateISO(swapped), needsReview: false };
      }
    }
    return { needsReview: true };
  }

  // Implausibly old → keep the value (so the user sees what was read) but require confirmation.
  if (startOfToday.getTime() - parsed.getTime() > MAX_RECEIPT_AGE_MS) {
    return { date: formatLocalDateISO(parsed), needsReview: true };
  }

  return { date: formatLocalDateISO(parsed), needsReview: false };
};

/**
 * Fallback: extract an embedded quantity/unit from a product name when the AI
 * left it in the name instead of moving it to quantity_estimate.
 *
 * e.g. "Lait 2L" → { cleanName: "Lait", quantity: 2, unit: "l" }
 * Returns null when no quantity pattern is found.
 */
export const extractQuantityFromName = (name: string): {
  cleanName: string;
  quantity: number;
  unit: string;
} | null => {
  // Multipack descriptors like "4x100g" / "2X250ml" describe the pack format,
  // not a purchasable quantity — never extract from them ("Câpres (4x100g)"
  // must NOT become 100 g of capers).
  // (?<![xX×\d.]) blocks a number that directly follows x/× or another digit.
  // Volume first: "2L", "2l", "1.5L", "500ml", "500 ml"
  // (?![a-zA-Z]) prevents matching "l" inside words like "Lentilles"
  const volumeMatch = name.match(/(?<![xX×\d.])(\d+(?:\.\d+)?)\s*(ml|l)(?![a-zA-Z])/i);
  if (volumeMatch) {
    const cleanName = name.replace(volumeMatch[0], '').trim().replace(/\s{2,}/g, ' ');
    const quantity = parseFloat(volumeMatch[1]);
    const unit = volumeMatch[2].toLowerCase();
    return { cleanName, quantity, unit };
  }

  // Weight: "250g", "1.5kg", "0.5 lb"
  const weightMatch = name.match(/(?<![xX×\d.])(\d+(?:\.\d+)?)\s*(kg|g|lb|lbs|oz)(?![a-zA-Z])/i);
  if (weightMatch) {
    const cleanName = name.replace(weightMatch[0], '').trim().replace(/\s{2,}/g, ' ');
    const quantity = parseFloat(weightMatch[1]);
    let unit = weightMatch[2].toLowerCase();
    if (unit === 'lbs') unit = 'lb';
    return { cleanName, quantity, unit };
  }

  return null;
};

/**
 * Scan forward from `open` (which must index a `{` or `[`) and return the index
 * of its matching close, or -1 if it never closes. String-aware: braces and
 * brackets inside JSON strings are ignored, as are escaped quotes.
 */
const matchingCloseIndex = (text: string, open: number): number => {
  const closers: Record<string, string> = { '{': '}', '[': ']' };
  const stack: string[] = [];
  let inString = false;
  let escaped = false;

  for (let i = open; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{' || ch === '[') stack.push(closers[ch]);
    else if (ch === '}' || ch === ']') {
      // A closer that doesn't match what we opened means the JSON is broken
      // here (this is exactly the `]` that ends detected_items arriving while
      // an item is still unclosed). Treat the span as unterminated.
      if (stack[stack.length - 1] !== ch) return -1;
      stack.pop();
      if (stack.length === 0) return i;
    }
  }
  return -1;
};

/** Parse the shortest balanced `{...}` starting at or after `from`, or null. */
const parseObjectAt = <T>(text: string, from: number): T | null => {
  const open = text.indexOf('{', from);
  if (open === -1) return null;
  const close = matchingCloseIndex(text, open);
  if (close === -1) return null;
  try {
    return JSON.parse(text.slice(open, close + 1)) as T;
  } catch {
    return null;
  }
};

/**
 * Last-resort recovery for a model response that isn't valid JSON.
 *
 * Two failure modes are seen in the wild, and this handles both:
 *  - **Truncation** — the model hit its output limit mid-JSON, leaving a clean
 *    prefix and no tail.
 *  - **A single malformed item** — the model dropped one closing brace in the
 *    middle of `detected_items` (issue #658). The earlier prefix-based repair
 *    lost the *whole band* to this, because one missing brace shifts the nesting
 *    depth of every item after it; when the broken item was the first one,
 *    nothing at all was recovered.
 *
 * So rather than cutting the document back to the last known-good prefix, every
 * item is extracted and parsed independently: a `{` that doesn't yield a
 * balanced, parseable object is skipped and the scan resumes at the next one.
 * Only the genuinely broken items are lost.
 *
 * Returns null when no item could be recovered.
 */
export const salvageReceiptJson = (text: string): ReceiptRecognitionResponse | null => {
  let cleaned = text.trim();
  if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\s*/, '');
  else if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\s*/, '');
  cleaned = cleaned.replace(/\s*```\s*$/, '');

  const itemsKey = cleaned.indexOf('"detected_items"');
  if (itemsKey === -1) return null;
  const arrayStart = cleaned.indexOf('[', itemsKey);
  if (arrayStart === -1) return null;

  // Walk the array region one candidate object at a time. On success, resume
  // after the object so a nested object (e.g. expiry_date) is never mistaken
  // for an item of its own.
  const detected_items: ReceiptDetectedItem[] = [];
  let cursor = arrayStart + 1;
  while (cursor < cleaned.length) {
    const open = cleaned.indexOf('{', cursor);
    if (open === -1) break;
    const close = matchingCloseIndex(cleaned, open);
    if (close === -1) {
      // Unterminated from here on: either the tail was truncated or this item
      // lost a brace. Skip past this `{` and try the next candidate.
      cursor = open + 1;
      continue;
    }
    try {
      const item = JSON.parse(cleaned.slice(open, close + 1)) as ReceiptDetectedItem;
      // `name` is what makes an object an item; anything else here is noise.
      if (item && typeof item.name === 'string') detected_items.push(item);
    } catch {
      // Malformed but balanced — drop this one only.
    }
    cursor = close + 1;
  }

  if (detected_items.length === 0) return null;

  const metaKey = cleaned.indexOf('"receipt_metadata"');
  const notesKey = cleaned.indexOf('"analysis_notes"');
  return {
    detected_items,
    ...(metaKey !== -1
      ? { receipt_metadata: parseObjectAt<ReceiptMetadata>(cleaned, metaKey) ?? undefined }
      : {}),
    ...(notesKey !== -1
      ? { analysis_notes: parseObjectAt<ReceiptAnalysisNotes>(cleaned, notesKey) ?? undefined }
      : {}),
  } as ReceiptRecognitionResponse;
};

// ── Deterministic post-parse validation ──────────────────────────────────────

/**
 * Lines that must never become pantry items, even if the model emits them:
 * instant rebates (TPD), voids (ANNUL), bottle deposits (CONSIGNE), enviro
 * fees (WECOFRAIS/ECOFRAIS), and total/tax rows.
 */
const RECEIPT_NOISE_RE = /(^|[^a-z])(tpd|annul|void|consigne|wecofrais|w?ecofrai|ecofrais|enviro|depot|deposit|rabais|sous[\s-]?total|subtotal|taxes?|tps|tvq|tvh|total)([^a-z]|$)/i;

const normalizeForMerge = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length > 2 && w.endsWith('s') ? w.slice(0, -1) : w))
    .join(' ');

const WEIGHT_UNITS = new Set(['g', 'kg', 'lb', 'oz']);
const VOLUME_UNITS = new Set(['ml', 'l', 'cup']);

const unitFamily = (unit: string | undefined): 'weight' | 'volume' | 'count' => {
  const u = (unit || 'piece').toLowerCase();
  if (WEIGHT_UNITS.has(u)) return 'weight';
  if (VOLUME_UNITS.has(u)) return 'volume';
  return 'count';
};

export interface ReceiptValidationResult {
  items: DetectedFoodItem[];
  warnings: string[];
  captureConfidence: 'high' | 'medium' | 'low';
}

export interface ReceiptValidationOptions {
  /**
   * How to combine two entries for the same product.
   *
   * 'sum' (default, single-pass scans): the model emits one entry per printed
   * line, so two entries mean two units were bought.
   *
   * 'max' (tiled scans): entries arrive from overlapping image bands, so the
   * same physical line is often reported twice. Summing would double-count it;
   * a band that genuinely saw two identical lines already reports quantity 2.
   */
  duplicateStrategy?: 'sum' | 'max';

  /**
   * Compare the sum of extracted prices against the printed total to grade
   * capture completeness. Off by default: `npm run eval:receipt` measures only
   * ~40% of extracted prices as correct (the price column is visually sheared
   * on a twisted receipt), so the comparison reports price error rather than
   * missing items — it fired "70% off" on a scan that captured 93% of the
   * receipt. Re-enable when price-exact approaches 100%.
   */
  priceSumCheck?: boolean;
}

/**
 * Deterministic safety net applied after the AI response is parsed:
 * 1. Drops fee/discount/void/total lines that leaked through as items.
 * 2. Drops negative-price entries (rebates/refunds).
 * 3. Merges duplicate entries for the same product (same normalized name,
 *    same unit family) by summing quantities — warehouse receipts print one
 *    line per unit and models often emit them as separate items.
 * 4. Sum-checks extracted prices against the printed receipt total to grade
 *    how complete the capture likely is.
 */
export const validateAndCleanReceipt = (
  items: DetectedFoodItem[],
  metadata?: ReceiptMetadata,
  options: ReceiptValidationOptions = {},
): ReceiptValidationResult => {
  const { duplicateStrategy = 'sum', priceSumCheck = false } = options;
  const warnings: string[] = [];

  const kept: DetectedFoodItem[] = [];
  const voided: DetectedFoodItem[] = [];
  for (const item of items) {
    const haystack = `${item.name} ${item.receipt_line || ''}`;
    if (RECEIPT_NOISE_RE.test(haystack)) {
      warnings.push(`Dropped non-item line captured as food: "${item.name}"`);
      continue;
    }
    const price = item.total_price ?? item.unit_price;
    if (typeof price === 'number' && price < 0) {
      warnings.push(`Dropped negative-price entry (rebate/refund): "${item.name}" (${price})`);
      voided.push(item);
      continue;
    }
    kept.push(item);
  }

  // Merge duplicates: same normalized name + same unit family.
  const merged = new Map<string, DetectedFoodItem>();
  for (const item of kept) {
    const key = `${normalizeForMerge(item.name)}|${unitFamily(item.unit)}`;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, { ...item });
      continue;
    }
    const addQty = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
    const baseQty = typeof existing.quantity === 'number' && existing.quantity > 0 ? existing.quantity : 1;
    existing.quantity = duplicateStrategy === 'max'
      ? Math.max(baseQty, addQty)
      : baseQty + addQty;
    (existing as { quantity_estimate?: unknown }).quantity_estimate = existing.quantity;
    if (typeof item.total_price === 'number') {
      existing.total_price = duplicateStrategy === 'max'
        ? Math.max(existing.total_price || 0, item.total_price)
        : (existing.total_price || 0) + item.total_price;
    }
    existing.confidence = Math.max(existing.confidence || 0, item.confidence || 0);
    warnings.push(`Merged duplicate "${item.name}" into quantity ${existing.quantity}`);
  }

  // Voided (ANNUL) lines the model emitted as negative-price entries: subtract
  // one unit (or the voided weight/volume) from the matching product instead of
  // leaving the bought count inflated.
  for (const v of voided) {
    const key = `${normalizeForMerge(v.name)}|${unitFamily(v.unit)}`;
    const target = merged.get(key);
    if (!target) continue;
    const voidQty = typeof v.quantity === 'number' && v.quantity > 0 ? v.quantity : 1;
    const targetQty = typeof target.quantity === 'number' && target.quantity > 0 ? target.quantity : 1;
    const remaining = targetQty - voidQty;
    if (remaining > 0) {
      target.quantity = remaining;
      (target as { quantity_estimate?: unknown }).quantity_estimate = remaining;
      if (typeof target.total_price === 'number' && typeof (v.total_price ?? v.unit_price) === 'number') {
        target.total_price = Math.max(0, target.total_price + (v.total_price ?? v.unit_price ?? 0));
      }
      warnings.push(`Voided line applied: "${v.name}" quantity reduced to ${remaining}`);
    } else {
      merged.delete(key);
      warnings.push(`Voided line applied: "${v.name}" fully cancelled`);
    }
  }

  const cleanedItems = Array.from(merged.values());

  // Sum-check against the printed total. Disabled by default — see
  // ReceiptValidationOptions.priceSumCheck: extracted prices are currently too
  // unreliable for this to indicate anything about capture completeness.
  let captureConfidence: 'high' | 'medium' | 'low' = 'high';
  if (priceSumCheck && metadata?.total_amount && metadata.total_amount > 0) {
    const itemSum = cleanedItems.reduce((sum, i) => sum + (i.total_price || 0), 0);
    const deviation = Math.abs(itemSum - metadata.total_amount) / metadata.total_amount;
    if (deviation > 0.15) captureConfidence = 'low';
    else if (deviation > 0.05) captureConfidence = 'medium';
    if (captureConfidence !== 'high') {
      warnings.push(
        `Sum check: items total ${itemSum.toFixed(2)} vs receipt total ${metadata.total_amount.toFixed(2)} (${(deviation * 100).toFixed(1)}% off) — some lines may be missing or misread`,
      );
    }
  }

  return { items: cleanedItems, warnings, captureConfidence };
};

/**
 * Convert receipt response to standard FoodRecognitionResponse
 */
export const convertReceiptToFoodRecognition = (
  receiptResponse: ReceiptRecognitionResponse
): FoodRecognitionResponse => {
  console.log('ReceiptProcessor - Converting receipt response with', receiptResponse.detected_items.length, 'items');

  // Convert detected items
  const detectedItems: DetectedFoodItem[] = receiptResponse.detected_items.map((item) => {
    // Parse quantity to get number and unit
    let { quantity, unit } = parseQuantityWithUnit(item.quantity_estimate);
    let name = item.name;

    // Fallback: if the AI left a volume/weight embedded in the name (e.g. "Lait 2L"
    // with quantity_estimate "1"), extract it deterministically and clean the name.
    if (unit === 'piece') {
      const extracted = extractQuantityFromName(item.name);
      if (extracted) {
        quantity = extracted.quantity;
        unit = extracted.unit;
        name = extracted.cleanName;
      }
    }

    return {
      name,
      category: mapReceiptCategory(item.category),
      confidence: item.confidence,
      quantity_estimate: quantity, // Store as number for backward compatibility
      quantity: quantity, // Pre-parsed numeric quantity (e.g., 1.195)
      unit: unit, // Pre-parsed unit (e.g., "kg", "g", "piece")
      visible_condition: 'good' as VisibleCondition, // Receipts don't have visual condition
      expiry_category: item.expiry_category, // NEW - simple unified category
      // Store receipt-specific data in the item for later use
      ...(item.unit_price && { unit_price: item.unit_price }),
      ...(item.total_price && { total_price: item.total_price }),
      ...(item.receipt_line && { receipt_line: item.receipt_line }),
      // "Frozen Foods" survives as a location because it cannot survive as a
      // category — see receiptCategoryLocation.
      ...(receiptCategoryLocation(item.category) && { location: receiptCategoryLocation(item.category) }),
    } as DetectedFoodItem;
  });

  // Map analysis notes quality ratings
  const mapQuality = (quality: string): 'Poor' | 'Fair' | 'Good' | 'Excellent' => {
    const normalized = quality.toLowerCase();
    if (normalized === 'poor') return 'Poor';
    if (normalized === 'fair') return 'Fair';
    if (normalized === 'good') return 'Good';
    if (normalized === 'excellent') return 'Excellent';
    return 'Fair'; // Default
  };

  // Validate/repair the AI-extracted purchase date so an obviously-wrong year (e.g. 2020)
  // never flows into inventory unchecked. needsReview drives the UI confirmation gate.
  let sanitizedMetadata: ReceiptMetadata | undefined;
  if (receiptResponse.receipt_metadata) {
    const { date, needsReview } = sanitizePurchaseDate(receiptResponse.receipt_metadata.purchase_date);
    sanitizedMetadata = {
      ...receiptResponse.receipt_metadata,
      purchase_date: date,
      purchase_date_needs_review: needsReview,
    };
    if (needsReview) {
      console.log('ReceiptProcessor - purchase_date flagged for review (raw:', receiptResponse.receipt_metadata.purchase_date, ')');
    }
  }

  // Deterministic safety net: drop fee/discount/void lines, merge duplicate
  // product entries, and grade completeness against the printed total.
  const validation = validateAndCleanReceipt(detectedItems, sanitizedMetadata ?? receiptResponse.receipt_metadata);
  if (validation.warnings.length > 0) {
    console.warn('ReceiptProcessor - validation:', validation.warnings);
  }

  // Convert to standard FoodRecognitionResponse. analysis_notes can be absent
  // when a truncated response was repaired — fall back to sane defaults.
  const notes = receiptResponse.analysis_notes;
  const result: FoodRecognitionResponse = {
    detected_items: validation.items,
    analysis_notes: {
      image_quality: mapQuality(notes?.image_quality ?? 'Fair'),
      lighting_conditions: mapQuality(notes?.text_readability ?? 'Fair'), // Use text readability as proxy
      total_items_detected: notes?.total_items_detected ?? validation.items.length,
      areas_analyzed: ['Receipt Items'], // Generic for receipts
    },
    validation_warnings: validation.warnings,
    capture_confidence: validation.captureConfidence,
    // Add receipt metadata if present
    ...(sanitizedMetadata && { receipt_metadata: sanitizedMetadata }),
  };

  console.log('ReceiptProcessor - Converted to', result.detected_items.length, 'food items');

  return result;
};

export default {
  getReceiptParserPrompt,
  convertReceiptToFoodRecognition,
};
